// Tutorial progress persistence — DB-backed with a localStorage fallback.
//
// The source of truth is the `tutorial_progress` table (one row per user, RLS
// scoped to auth.uid()), so progress follows the advisor across devices. Until
// the migration is applied to production — or if the DB is unreachable — the
// same shape lives in localStorage so the tutorial still works end to end.
// Writes go to both; reads prefer the DB and fall back silently.
//
// Impersonation is safe by construction: writes go through safeSupabase, which
// no-ops all mutations while sandbox mode is active.

import { supabase } from '@/lib/safeSupabase';

export interface TutorialProgress {
  completed: string[];
  dismissed: boolean;
}

export const EMPTY_PROGRESS: TutorialProgress = { completed: [], dismissed: false };

interface ProgressRow {
  user_id: string;
  completed_modules: string[];
  dismissed: boolean;
}

// `tutorial_progress` isn't in the auto-generated Supabase types yet (Drew
// regenerates types after applying the migration). This narrow structural cast
// keeps the file type-safe without touching src/integrations/supabase (rule #4).
interface TutorialTable {
  select(columns: string): {
    eq(column: string, value: string): {
      maybeSingle(): Promise<{ data: ProgressRow | null; error: unknown }>;
    };
  };
  upsert(row: ProgressRow): Promise<{ error: unknown }>;
}

function table(): TutorialTable {
  return (supabase as unknown as { from(t: string): TutorialTable }).from('tutorial_progress');
}

const localKey = (userId: string) => `ttg-tutorial-${userId}`;

function loadLocal(userId: string): TutorialProgress {
  try {
    const raw = localStorage.getItem(localKey(userId));
    if (!raw) return { ...EMPTY_PROGRESS };
    const parsed = JSON.parse(raw) as Partial<TutorialProgress>;
    return {
      completed: Array.isArray(parsed.completed) ? parsed.completed.filter(m => typeof m === 'string') : [],
      dismissed: parsed.dismissed === true,
    };
  } catch {
    return { ...EMPTY_PROGRESS };
  }
}

function saveLocal(userId: string, progress: TutorialProgress): void {
  try {
    localStorage.setItem(localKey(userId), JSON.stringify(progress));
  } catch {
    // Storage unavailable (private mode, quota) — DB write still carries it.
  }
}

/** Load progress for a user: the DB row merged with this browser's copy. A dismissal or a
 *  finished module recorded in either place counts, so a stale DB row (a write that never
 *  landed, e.g. one made while impersonating, where safeSupabase drops every write) can't
 *  bring the welcome dialog back after the advisor said no. Restart clears both. */
export async function loadProgress(userId: string): Promise<TutorialProgress> {
  const local = loadLocal(userId);
  try {
    const { data, error } = await table()
      .select('user_id, completed_modules, dismissed')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return local;
    return {
      completed: [...new Set([...(data.completed_modules ?? []), ...local.completed])],
      dismissed: data.dismissed === true || local.dismissed,
    };
  } catch {
    return local;
  }
}

/** Persist progress. localStorage synchronously, DB best-effort. */
export function saveProgress(userId: string, progress: TutorialProgress): void {
  saveLocal(userId, progress);
  void (async () => {
    try {
      await table().upsert({
        user_id: userId,
        completed_modules: progress.completed,
        dismissed: progress.dismissed,
      });
    } catch {
      // Table missing (migration pending) or network hiccup — localStorage holds.
    }
  })();
}

// ─── Walkthrough started from Settings ─────────────────────────────────────
//
// Settings → Walkthroughs → Start replays one walkthrough on its own, whatever the
// person answered on the welcome dialog. Its progress lives in sessionStorage, not
// the DB row: a Start is a same-browser action, the DB write behind it is
// best-effort and may land after the next page mounts (Downline Management has
// its own provider), and a replay must never depend on which modules the row
// already lists as done. It ends when every module of the walkthrough has
// played, or when the tab closes.

export interface ActiveWalkthrough {
  id: string;
  /** Modules of this walkthrough that have played since Start. */
  completed: string[];
  /** Walkthroughs still to come after this one (the full tour), in order. */
  chain?: string[];
  /** Set when this record was reached by the chain, not by a Start button: the
   *  host has not yet been told to put the person on its first surface. */
  fromChain?: boolean;
}

const ACTIVE_KEY = 'ttg-walkthrough-active';

export function loadActiveWalkthrough(): ActiveWalkthrough | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveWalkthrough>;
    if (typeof parsed.id !== 'string' || !parsed.id) return null;
    const strings = (v: unknown) => (Array.isArray(v) ? v.filter((m): m is string => typeof m === 'string') : []);
    return {
      id: parsed.id,
      completed: strings(parsed.completed),
      ...(Array.isArray(parsed.chain) ? { chain: strings(parsed.chain) } : {}),
      ...(parsed.fromChain ? { fromChain: true } : {}),
    };
  } catch {
    return null;
  }
}

export function saveActiveWalkthrough(active: ActiveWalkthrough | null): void {
  try {
    if (active) sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(active));
    else sessionStorage.removeItem(ACTIVE_KEY);
  } catch {
    // Storage unavailable — the in-memory copy carries the current page.
  }
}
